import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  IconButton,
  Img,
  Input,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { MatrixCellPhoto } from '../../../variant/matrix/MatrixCellPhoto';
import { Composition } from '@texas/api/endpoints/compositionApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { GarpCellFormContext } from '../SetGarpDataModal';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { materialsApi } from '@texas/api/endpoints/metadata/materialsApi';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { Controller } from 'react-hook-form';
import { techniquesApi } from '@texas/api/endpoints/metadata/techniquesApi';
import { AutoUpdateWrapper } from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { useTranslation } from 'react-i18next';
import { Icons } from '@texas/components/shared/Icons';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { sum } from '@texas/utils/helpers/commonHelpers';
import { extractCombinedMaterial } from '../shared/helper';
import {
  Colors,
  Dimensions,
  Materials,
  Techniques,
  Weight,
} from '../shared/Components';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { clientEndpoints } from '@texas/clientEndpoints';
import { ExpandImage } from '@texas/components/shared/image/ExpandImage';
import { fileEvents } from '@texas/components/shared/dropzone/events';
import { WeightInput } from '@texas/components/shared/weight/WeightInput';
import {
  garpWeightRules,
  isValidStoredWeight,
  roundWeightForStorage,
} from '@texas/resources/weightUnits';

const flexGap = 2;

export function CellPhotoRow({
  variantId,
  folderId,
  cellX,
  cellY,
  paFileIdentifier,
  paFileId,
}: {
  variantId: number;
  folderId: number;
  cellX: number;
  cellY: number;
  paFileIdentifier?: string | null;
  paFileId?: number | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { request: updateRequest, loading } = useApiRequest(
    variantsApi.updatePhoto,
  );
  const [cellPhotoId, setCellPhotoId] = useState<number | null>(null);

  const copyPaPhoto = useCallback(() => {
    if (!paFileId || !paFileIdentifier) return;
    request(
      updateRequest,
      [variantId, paFileId, cellX, cellY],
      (_) => {
        toast({
          title: t('fileBrowser.updatedFile'),
          status: 'success',
          isClosable: true,
        });
        fileEvents.matrixFileChanged.dispatch({
          matrixNodeValueXId: cellX,
          matrixNodeValueYId: cellY,
          file: {
            id: paFileId,
            identifier: paFileIdentifier,
            archived: false,
            name: '',
            extension: '',
          },
        });
      },
      (_) => {
        toast({
          title: t('fileBrowser.updateFileFailed'),
          status: 'error',
          isClosable: true,
        });
      },
    );
  }, [
    paFileId,
    paFileIdentifier,
    updateRequest,
    variantId,
    cellX,
    cellY,
    toast,
    t,
  ]);

  return (
    <>
      <GridItem gridColumn="pa">
        {paFileIdentifier && (
          <Box w="fit-content" margin="auto">
            <ExpandImage variant="scale" identifier={paFileIdentifier}>
              <Img
                src={clientEndpoints.previewImage(paFileIdentifier, 312)}
                objectFit="cover"
                maxW="100%"
              />
            </ExpandImage>
          </Box>
        )}
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={1}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {paFileId != null && cellPhotoId !== paFileId && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              isLoading={loading}
              isDisabled={loading}
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={copyPaPhoto}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <MatrixCellPhoto
          variantId={variantId}
          cellX={cellX}
          cellY={cellY}
          folderId={folderId}
          onPhotoFetched={(fileId) => setCellPhotoId(fileId)}
        />
      </GridItem>
    </>
  );
}

export function CellSupplierSizeRow({
  compositions,
}: {
  compositions: Composition[];
}) {
  const { t } = useTranslation();
  const { register, triggerSubmit, registerAutoUpdate, watch, formState } =
    useActiveContext(GarpCellFormContext);

  const watchedValue = watch('supplierSize');

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('configuration.dimensions')}</Text>
        <Dimensions compositions={compositions} />
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="supplierSize"
          boxProps={{ flexGrow: '1' }}
        >
          <FormControl>
            <FormLabel>{t('garp.supplierSize')}</FormLabel>
            <Input
              {...register('supplierSize', { onBlur: triggerSubmit })}
              variant="texas"
            />
            <FormCounter length={(watchedValue ?? '').length} maxLength={20} />
            <ErrorLabel text={formState.errors.supplierSize?.message} />
          </FormControl>
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}

export function CellSupplierColors({
  compositions,
}: {
  compositions: Composition[];
}) {
  const { t } = useTranslation();
  const { register, triggerSubmit, registerAutoUpdate, watch, formState } =
    useActiveContext(GarpCellFormContext);

  const watchedValue = watch('supplierColorCode');

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('general.colors')}</Text>
        <Colors compositions={compositions} />
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="supplierColorCode"
          boxProps={{ flexGrow: '1' }}
        >
          <FormControl>
            <FormLabel>{t('garp.supplierColorCode')}</FormLabel>
            <Input
              {...register('supplierColorCode', {
                onBlur: triggerSubmit,
              })}
              variant="texas"
            />
            <FormCounter length={(watchedValue ?? '').length} maxLength={20} />
            <ErrorLabel text={formState.errors.supplierColorCode?.message} />
          </FormControl>
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}

export function CellWeight({
  rowIndex,
  compositions,
}: {
  rowIndex: number;
  compositions: Composition[];
}) {
  const { t } = useTranslation();
  const { control, registerAutoUpdate, triggerSubmit, setValue, watch } =
    useActiveContext(GarpCellFormContext);

  const watchedWeight = watch('weight');
  const totalWeight = useMemo(() => {
    return sum(compositions.flatMap((c) => c.materials.map((m) => m.weight)));
  }, [compositions]);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('composition.weight')}</Text>
        <Weight compositions={compositions} />
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {roundWeightForStorage(
          totalWeight,
          garpWeightRules.decimalPlaces,
          garpWeightRules.minimumPositiveWeight,
        ) !== watchedWeight && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue(
                  'weight',
                  roundWeightForStorage(
                    totalWeight,
                    garpWeightRules.decimalPlaces,
                    garpWeightRules.minimumPositiveWeight,
                  ),
                );
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="weight"
          boxProps={{ flexGrow: '1' }}
        >
          {({ isDisabled }) => (
            <Controller
              name="weight"
              control={control}
              rules={{
                validate: (value) =>
                  isValidStoredWeight(value, garpWeightRules.decimalPlaces) ||
                  t('weightConverter.invalidWeight'),
              }}
              render={({ field, fieldState }) => (
                <WeightInput
                  {...field}
                  isDisabled={isDisabled}
                  rules={garpWeightRules}
                  error={fieldState.error?.message}
                  onCommit={triggerSubmit}
                />
              )}
            />
          )}
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}

export function CellMaterial({
  rowIndex,
  compositions,
}: {
  rowIndex: number;
  compositions: Composition[];
}) {
  const { t } = useTranslation();
  const { control, registerAutoUpdate, triggerSubmit, watch, setValue } =
    useActiveContext(GarpCellFormContext);
  const { data, loading, refetch } = useApiResource(materialsApi.getAll);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const watchedMaterial = watch('materialId');
  const setMaterialId = useMemo(() => {
    if (!data) {
      return null;
    }

    const combinedMaterials = compositions.flatMap((c) =>
      c.materials.map((m) => m.material.id),
    );

    return extractCombinedMaterial(data, combinedMaterials)?.id ?? null;
  }, [compositions, data]);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('general.materials')}</Text>
        <Materials
          compositions={compositions}
          metadataField="articleTextField"
        />
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {setMaterialId !== watchedMaterial && setMaterialId && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue('materialId', setMaterialId);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="materialId"
          boxProps={{ flexGrow: '1' }}
        >
          <FormControl>
            <FormLabel>{t('general.material')}</FormLabel>
            <Controller
              name="materialId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  onBlur={triggerSubmit}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  menuPlacement="auto"
                  isLoading={loading}
                  isClearable={true}
                  options={data?.map((m) => ({
                    label: `${m.name} (${m.code})`,
                    value: m.id,
                  }))}
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: data?.find((d) => d.id === field.value)?.name,
                        }
                      : undefined
                  }
                />
              )}
            />
          </FormControl>
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}

export function CellTechnique({
  compositions,
}: {
  compositions: Composition[];
}) {
  const { t } = useTranslation();
  const { control, registerAutoUpdate, triggerSubmit } =
    useActiveContext(GarpCellFormContext);
  const { data, loading, refetch } = useApiResource(techniquesApi.getAll);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('configuration.techniques')}</Text>
        <Techniques compositions={compositions} />
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="techniqueId"
          boxProps={{ flexGrow: '1' }}
        >
          <FormControl>
            <FormLabel>{t('general.technique')}</FormLabel>
            <Controller
              name="techniqueId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  onBlur={triggerSubmit}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  menuPlacement="auto"
                  isLoading={loading}
                  isClearable={true}
                  options={data?.map((m) => ({ label: m.value, value: m.id }))}
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: data?.find((d) => d.id === field.value)?.value,
                        }
                      : undefined
                  }
                />
              )}
            />
          </FormControl>
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}
